<template>
  <section>
    <div class="modal-card" style="width: 600px">
      <header class="modal-card-head">
        <p class="modal-card-title">Enviar a Comisión Precalificadora</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <!-- Mensaje informativo -->
        <b-message type="is-info" has-icon>
          Se validará que las actividades que exceden las horas comprometidas tengan al menos una evidencia
          adjunta antes de enviar a precalificación.
        </b-message>

        <!-- Validación de evidencias -->
        <div v-if="validating" class="has-text-centered my-5">
          <b-loading :is-full-page="false" v-model="validating" />
          <p class="mt-3">Validando evidencias...</p>
        </div>

        <!-- Resultado de validación -->
        <div v-if="!validating && validationResult !== null">
          <!-- Todo OK sin advertencias -->
          <div v-if="validationResult.success && !validationResult.hasWarning">
            <b-message type="is-success" has-icon>
              ✓ Las actividades que exceden horas comprometidas tienen evidencias. Puede proceder a
              enviar.
            </b-message>
            <p class="mt-4">
              Se enviará un código de verificación a su correo:
            </p>
            <p class="has-text-centered has-text-weight-bold mt-2">
              {{ email }}
            </p>
          </div>

          <!-- OK pero con advertencia: Actividades con 0 horas -->
          <div v-else-if="validationResult.success && validationResult.hasWarning">
            <b-message type="is-warning" has-icon>
              ⚠️ Se detectaron actividades con 0 horas declaradas.
            </b-message>

            <div class="box mt-4" style="max-height: 300px; overflow-y: auto">
              <p class="has-text-weight-bold mb-3">
                Actividades sin horas declaradas:
              </p>
              <ul>
                <li
                  v-for="(activity, index) in activitiesWithZeroHours"
                  :key="index"
                  class="mb-2"
                >
                  <span class="icon has-text-warning">
                    <i class="fas fa-exclamation-triangle"></i>
                  </span>
                  <span>{{ activity }}</span>
                </li>
              </ul>
            </div>

            <b-message type="is-info" class="mt-4">
              Si no realizó estas actividades, puede continuar con 0 horas. De lo contrario, 
              vuelva a declarar las horas correspondientes.
            </b-message>

            <p class="mt-4">
              Se enviará un código de verificación a su correo:
            </p>
            <p class="has-text-centered has-text-weight-bold mt-2">
              {{ email }}
            </p>
          </div>

          <!-- ERROR: Faltan evidencias -->
          <div v-else>
            <b-message type="is-danger" has-icon>
              ✗ Algunas actividades que exceden horas comprometidas no tienen evidencias adjuntas.
            </b-message>

            <div class="box mt-4" style="max-height: 300px; overflow-y: auto">
              <p class="has-text-weight-bold mb-3">
                Actividades que exceden horas sin evidencias:
              </p>
              <ul>
                <li
                  v-for="(activity, index) in activitiesWithoutEvidence"
                  :key="index"
                  class="mb-2"
                >
                  <span class="icon has-text-danger">
                    <i class="fas fa-exclamation-circle"></i>
                  </span>
                  <span>{{ activity }}</span>
                </li>
              </ul>
            </div>

            <b-message type="is-warning" class="mt-4">
              Debe adjuntar evidencias a las actividades que exceden las horas comprometidas antes de enviar
              a precalificación.
            </b-message>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot">
        <div class="buttons is-right" style="width: 100%">
          <b-button type="is-light" @click="$emit('close')">
            Cancelar
          </b-button>

          <b-button
            v-if="
              !validating &&
              validationResult !== null &&
              !validationResult.success
            "
            type="is-warning"
            @click="$emit('close')"
          >
            Ir a adjuntar evidencias
          </b-button>

          <b-button
            v-if="
              !validating &&
              validationResult !== null &&
              validationResult.success
            "
            type="is-success"
            icon-left="paper-plane"
            @click="sendToCommittee"
            :loading="sending"
          >
            Enviar a Comisión
          </b-button>
        </div>
      </footer>
    </div>
  </section>
</template>

<script>
import axios from "axios";

export default {
  name: "ModalSendToCommittee",

  props: {
    performanceId: {
      type: Number,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      validating: false,
      sending: false,
      validationResult: null,
      activitiesWithoutEvidence: [],
      activitiesWithZeroHours: [],
    };
  },

  mounted() {
    // Validar automáticamente al abrir el modal
    this.validateEvidence();
  },

  methods: {
    async validateEvidence() {
      this.validating = true;
      this.validationResult = null;
      this.activitiesWithoutEvidence = [];
      this.activitiesWithZeroHours = [];

      try {
        // Usar el endpoint de solo validación (NO envía correo)
        const response = await axios.post(
          `/performances/${this.performanceId}/validate_for_committee.json`
        );

        // Validar si hay actividades con 0 horas (advertencia, no bloqueo)
        if (response.data.activities_with_zero_hours && 
            response.data.activities_with_zero_hours.length > 0) {
          this.activitiesWithZeroHours = response.data.activities_with_zero_hours;
          
          // Mostrar confirmación pero permitir continuar
          this.validationResult = {
            success: true,
            hasWarning: true,
            message: response.data.message,
          };
        } else {
          // Si la respuesta es exitosa y no hay advertencias, todo OK
          this.validationResult = {
            success: true,
            hasWarning: false,
            message: response.data.message,
          };
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 422) {
          // Faltan evidencias
          this.validationResult = {
            success: false,
            message: error.response.data.error,
          };

          // Extraer lista de actividades sin evidencias
          const errorMsg = error.response.data.error;
          if (error.response.data.activities_without_evidence) {
            this.activitiesWithoutEvidence =
              error.response.data.activities_without_evidence
                .split(", ")
                .filter((x) => x);
          }
        } else if (error.response && error.response.status === 403) {
          // Error de fase incorrecta
          this.$buefy.dialog.alert({
            title: "Error",
            message:
              error.response.data.error ||
              "No es posible enviar a precalificación en esta fase.",
            type: "is-danger",
            hasIcon: true,
            icon: "times-circle",
            iconPack: "fa",
          });
          this.$emit("close");
        } else {
          // Error genérico
          this.$buefy.dialog.alert({
            title: "Error",
            message: "Hubo un error al validar las evidencias.",
            type: "is-danger",
            hasIcon: true,
            icon: "times-circle",
            iconPack: "fa",
          });
        }
      } finally {
        this.validating = false;
      }
    },

    async sendToCommittee() {
      this.sending = true;

      try {
        // AHORA SÍ enviar el correo con el código
        const response = await axios.post(
          `/performances/${this.performanceId}/send_to_committee.json`
        );

        this.validationResult.verification_code_id = response.data.verification_code_id;
        
        // Cerrar este modal y abrir el de confirmación
        this.$emit("close");
        this.$emit("open-verification-modal", this.validationResult);
      } catch (error) {
        console.error(error);
        this.$buefy.dialog.alert({
          title: "Error",
          message: error.response?.data?.error || "Hubo un error al enviar el código de verificación.",
          type: "is-danger",
          hasIcon: true,
          icon: "times-circle",
          iconPack: "fa",
        });
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-card-foot .buttons {
  justify-content: flex-end;
}
</style>
